import React, { useState, useEffect, useRef } from "react";
import { useMountEffect } from 'primereact/hooks';
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Langs from "../../app/lang/langs";
import styles from "./Dashboard.module.css";
import { setHasUpdates } from "../common/commonSlice";
import { getDashboardData, setPendingLeaves } from "./dashboardSlice";
import { getLeaves, getMyLeaves } from "../leaves/leavesSlice";
import { getCollabs } from "../../components/collabs/collabsSlice";
import { checkPermission, showErrorToast } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import MobileDashboardView from "./MobileDashboardView";
import WebDashboardView from "./WebDashboardView";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import DashboardNotificationsButton from "./web/widgets/DashboardNotificationsButton";
import DashboardVSecuriteNotificationsButton from "./web/widgets/DashboardVSecuriteNotificationsButton";
import DashboardNewsDialog from "./web/widgets/DashboardNewsDialog";
import { fetchCollabPendingAwareness, fetchCollabPendingQuizzes } from "../../components/vsecurite/vsecuriteSlice";
import AddNewsModal from "../../components/dashboard/News/AddNewsModal";
import { TeamSelector } from "../../components/teams/TeamSelector";


export function DashboardView() {

    const dispatch = useDispatch()
    const [selectedNews, setSelectedNews] = useState(null)
    const [newsDialogOpen, setNewsDialogOpen] = useState(false)
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const [addNewsOpen, setAddNewsOpen] = useState(false)
    const isMenuOpen = Boolean(menuAnchorEl)
    const canEditNews = checkPermission(config.scopes.news, config.permissionTypes.edit)
    const userRoleId = useSelector((state) => state.login.userRoleId)
    const permissions = useSelector((state) => state.login.permissions)
    const canSelectTeam = React.useMemo(() => {
        if (typeof userRoleId === "undefined") return true;
        if (parseInt(userRoleId) <= 2) return true;
        if (!Array.isArray(permissions)) return false;
        return permissions.some((perm) =>
            perm &&
            perm.id_team != null &&
            perm.scope === config.scopes.leaves &&
            (perm.type === config.permissionTypes.view || perm.type === config.permissionTypes.edit)
        );
    }, [permissions, userRoleId]);
    const newsList = useSelector((state) => state.dashboard.news)
    const notifications = useRef(null)
    const mounted = useRef(null)
    const lastNewsId = useSelector((state) => state.dashboard.lastNewsId)

    const leaves = useSelector((state) => state.leaves.list)
    const collabs = useSelector((state) => state.collabs.currentList)
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds)
    const isMobile = useSelector((state) => state.settings.isMobile)

    function handleForbidden(action) {
        const status = action?.payload?.responseError?.status;
        if (status === 403) {
            showErrorToast("Acces refuse : equipe non autorisee.");
        }
    }

    function loadLeaves() {
        if (selectedTeamsIds && selectedTeamsIds.length && collabs.length) {
            const canViewLeaveInsights = checkPermission(
                config.scopes.leaves,
                config.permissionTypes.view,
                selectedTeamsIds && selectedTeamsIds.length ? selectedTeamsIds : null
            );
            if (!canViewLeaveInsights) {
                return;
            }
            let selectedCollabsIds = collabs.reduce((array, current) => {
                array.push(current.id)
                return array
            }, [])
            dispatch(getLeaves({ selectedTeamsIds, selectedCollabsIds }))
                .then(handleForbidden)
        }
    }
    // V-Sécurité admins author campaigns and quizzes; they don't need
    // collab-side reminders, so we skip the fetch (and hide the button).
    const isVsecAdmin = checkPermission(config.scopes.vsecurite, config.permissionTypes.edit)

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true
            const boolHasUpdates = Boolean(newsList?.some(item => item.seen === null))
            dispatch(setHasUpdates(boolHasUpdates))
            dispatch(getDashboardData({ lastNewsId: lastNewsId }))
            loadLeaves()
            dispatch(getMyLeaves({ monthsCount: 6 }))
            if (!isVsecAdmin) {
                dispatch(fetchCollabPendingAwareness())
                dispatch(fetchCollabPendingQuizzes())
            }
        }
    }, [])
    
    useEffect(() => {
        if (selectedTeamsIds && selectedTeamsIds.length) {
            dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }))
                .then(handleForbidden)
        }
    }, [dispatch, selectedTeamsIds])

    useEffect(() => {
        loadLeaves()
    }, [selectedTeamsIds, collabs])

    useEffect(() => {
        const boolHasUpdates = Boolean(newsList?.some(item => item.seen === null))
        dispatch(setHasUpdates(boolHasUpdates))
    }, [newsList])

    useEffect(() => {
        if (leaves) {
            const pendingLeaves = leaves.filter(leave => leave.status === "PENDING").length;
            dispatch(setPendingLeaves(pendingLeaves));
        }
    }, [leaves])


    useMountEffect(() => {
        if (notifications.current) {
            notifications.current.clear();
            notifications.current.show([
                { sticky: true, severity: 'info', summary: 'Info', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'success', summary: 'Success', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'warn', summary: 'Warning', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'error', summary: 'Error', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'success', summary: 'Success', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'info', summary: 'Info', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'error', summary: 'Error', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'info', summary: 'Info', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'success', summary: 'Success', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'warn', summary: 'Warning', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'error', summary: 'Error', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'success', summary: 'Success', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'info', summary: 'Info', detail: 'Message Content', closable: true },
                { sticky: true, severity: 'error', summary: 'Error', detail: 'Message Content', closable: true },
            ])
        }
    })


    const unreadNewsCount = newsList ? newsList.filter(item => item.seen === null).length : 0;

    const onNewsSelect = (e) => {
        setSelectedNews(e.data)
    }

    return (
        <div>
            {isMobile === 2 ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        bgcolor: "rgba(255,255,255,0.72)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        boxShadow: "0 1px 8px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
                        px: 1,
                        pt: 0.5,
                        pb: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minHeight: 48,
                            px: 0.5,
                        }}
                    >
                        <Box sx={{ fontWeight: 650, fontSize: "1.1rem", letterSpacing: "-0.02em", pl: 0.5 }}>
                            <Langs str="Dashboard" />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {canEditNews && (
                                <>
                                    <IconButton
                                        aria-label="actions actualités"
                                        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                                        sx={{
                                            minWidth: 44,
                                            minHeight: 44,
                                            borderRadius: "12px",
                                            border: "1px solid rgba(255, 144, 21, 0.18)",
                                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                                            backdropFilter: "blur(12px)",
                                            WebkitBackdropFilter: "blur(12px)",
                                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": { backgroundColor: "rgba(255,144,21,0.08)", transform: "scale(1.06)", boxShadow: "0 2px 8px rgba(255,144,21,0.15)" },
                                            "&:active": { transform: "scale(0.96)" },
                                        }}
                                    >
                                        <AddIcon sx={{ color: "#FF9015", fontSize: 22 }} />
                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchorEl}
                                        open={isMenuOpen}
                                        onClose={() => setMenuAnchorEl(null)}
                                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    mt: 1,
                                                    borderRadius: "12px",
                                                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => {
                                                setMenuAnchorEl(null);
                                                setAddNewsOpen(true);
                                            }}
                                        >
                                            Ajouter une actualité
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                            {!isVsecAdmin && <DashboardVSecuriteNotificationsButton />}
                            <DashboardNotificationsButton
                                count={unreadNewsCount}
                                onClick={() => setNewsDialogOpen(true)}
                            />
                        </Box>
                    </Box>
                    {canSelectTeam && (
                        <Box sx={{ px: 0.5 }}>
                            <TeamSelector includeMyTeam />
                        </Box>
                    )}
                </Box>
            ) : (
                <div className={styles.topMenu}>
                    <Row style={{ width: "100vw" }}>
                        <Col md={6}>
                            <Box
                                className="d-flex align-items-center gap-3"
                                sx={{
                                    "& .headerTitle": {
                                        letterSpacing: "-0.02em",
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                <span className="headerTitle text-nowrap">
                                    {<Langs str="Dashboard" />}
                                </span>
                                {canSelectTeam && <TeamSelector includeMyTeam />}
                            </Box>
                        </Col>
                        <Col md={6} className='d-flex justify-content-end align-items-center gap-2'>
                            {canEditNews && (
                                <>
                                    <IconButton
                                        aria-label="actions actualités"
                                        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                                        sx={{
                                            borderRadius: "12px",
                                            border: "1px solid rgba(255, 144, 21, 0.18)",
                                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                                            backdropFilter: "blur(12px)",
                                            WebkitBackdropFilter: "blur(12px)",
                                            boxShadow: "0 4px 16px rgba(255, 144, 21, 0.08), 0 1px 3px rgba(0, 40, 128, 0.04)",
                                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                            "&:hover": {
                                                transform: "scale(1.05)",
                                                backgroundColor: "rgba(255, 144, 21, 0.08)",
                                                boxShadow: "0 8px 24px rgba(255, 144, 21, 0.16), 0 2px 6px rgba(0, 40, 128, 0.06)",
                                                border: "1px solid rgba(255, 144, 21, 0.32)",
                                            },
                                            "&:active": { transform: "scale(0.97)" },
                                        }}
                                    >
                                        <AddIcon sx={{ color: "#FF9015", fontSize: 22 }} />
                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchorEl}
                                        open={isMenuOpen}
                                        onClose={() => setMenuAnchorEl(null)}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    mt: 1,
                                                    borderRadius: "14px",
                                                    border: "1px solid rgba(255, 144, 21, 0.12)",
                                                    backgroundColor: "rgba(255, 255, 255, 0.85)",
                                                    backdropFilter: "blur(16px)",
                                                    WebkitBackdropFilter: "blur(16px)",
                                                    boxShadow: "0 12px 40px rgba(255, 144, 21, 0.1), 0 4px 12px rgba(0, 40, 128, 0.06)",
                                                    overflow: "visible",
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => { setMenuAnchorEl(null); setAddNewsOpen(true); }}
                                            sx={{
                                                borderRadius: "10px",
                                                mx: 0.5, py: 1.2, px: 2,
                                                fontWeight: 400,
                                                letterSpacing: "0.01em",
                                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                                "&:hover": { backgroundColor: "rgba(255, 144, 21, 0.08)" },
                                            }}
                                        >
                                            Ajouter une actualité
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                            {!isVsecAdmin && <DashboardVSecuriteNotificationsButton />}
                            <DashboardNotificationsButton
                                count={unreadNewsCount}
                                onClick={() => setNewsDialogOpen(true)}
                            />
                        </Col>
                    </Row>
                </div>
            )}


            <div className={styles.bodyContainer}>
                {isMobile === 2 ? (
                    <MobileDashboardView />
                ) : (
                    <WebDashboardView />
                )}
            </div>

            <DashboardNewsDialog open={newsDialogOpen} onClose={() => setNewsDialogOpen(false)} />
            {canEditNews && <AddNewsModal visible={addNewsOpen} setVisible={setAddNewsOpen} />}
        </div>
    )
}

